import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenAI from "openai";
import { motion, AnimatePresence } from 'framer-motion';
import { Tools } from 'unifai-sdk';
import Anthropic from '@anthropic-ai/sdk';
import MarkdownRenderer from './MarkdownRenderer.js'
import ShareButtons from './common/ShareButtons';

// Import necessary image resources
import bgMain from "../images/bg-main.png";
import pinaiLogo from "../images/pinai_logo.png";

// Import project  data
import honestScoreReport from "../data/binance_project_report/honest_score_report.json";

const createAnthropicTools = (tools) => {
  return tools.map(tool => {
    if (tool.type === 'function') {
      const { name, description } = tool.function;
      const { parameters } = tool.function;
      
      return {
        name,
        description,
        input_schema: parameters
      };
    }
    return tool; // Return as is if not a function type
  });
};

const createUnifaiTools = (tools) => {
  return tools.map(tool => {
    return {
      id: tool.id,
      type: 'function',
      function: {
        name: tool.name,
        arguments: JSON.stringify(tool.input)
      }
    };
  });
};

const DeepResearchDetail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form' or 'result'
  const [tokenData, setTokenData] = useState({
    projectName: '',
    ticker: '',
    year: '',
  });
  const [result, setResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [thinkingResult, setThinkingResult] = useState('');
  const [writingReport, setWritingReport] = useState(false);
  // Always show thinking panel by default
  const [manualThinkingPanel, setManualThinkingPanel] = useState(false);
  // State for honest score explanation panel
  const [showHonestScorePanel, setShowHonestScorePanel] = useState(false);
  // 用于分享图片的唯一ID
  const resultCardId = 'deep-research-result-card';
  // Group projects by year for initial state
  const initialProjectsByYear = {};
  honestScoreReport.documents.forEach(project => {
    const year = project.year;
    if (!initialProjectsByYear[year]) {
      initialProjectsByYear[year] = [];
    }
    initialProjectsByYear[year].push(project);
  });
  
  const [filteredProjects, setFilteredProjects] = useState(initialProjectsByYear);
  const thinkingContentRef = useRef(null);

  // Move API client initialization to component level instead of recreating in each function
  const tools = useRef(new Tools({ 
    apiKey: process.env.REACT_APP_UNIFAI_AGENT_API_KEY || 'zOdyjv8Ry7QBIZOp62JeGFRzp0gqCavnhs0s6UIVJFG'
  }));
  const anthropic = useRef(new Anthropic({
    apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY || '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',
    dangerouslyAllowBrowser: true
  }));
  const openai = useRef(new OpenAI({
    apiKey: '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',
    dangerouslyAllowBrowser: true
  }));

  // Auto-scroll the thinking content panel to the bottom when content changes
  useEffect(() => {
    if (thinkingContentRef.current && thinkingResult) {
      thinkingContentRef.current.scrollTop = thinkingContentRef.current.scrollHeight;
    }
  }, [thinkingResult]);

  /**
   * Generates a report using Unifai tools
   * @param {Object} project - The project data
   * @param {string} fullResponse - The full response string
   * @param {AbortController} abortController - The abort controller
   * @returns {Promise<string>} The generated report
   */
  const generateUnifaiReport = async (project, fullResponse, abortController) => {
    // Build system prompt
    let systemPrompt = `
  You are a personal assistant capable of doing many things with your tools.
  When you are given a task you cannot do (like something you don't know,
  or requires you to take some action), try to find appropriate tools to do it.
  Please limit the number of tools you use to be less than 6. This is a hard constraint, please do not exceed 4 tools.
  `;

  // Initialize messages array
    let userPrompt = `
  Retrieve basic token information and real-time market information for ${project.projectName} (ticker: ${project.ticker}). 
  The report should only have two sections: "Basic Token Information" and "Real-time Market Information".
  Please limit the number of tool calls you use to be less than 6. This is a hard constraint, please do not exceed 6 tools.
  Make sure to include the "---" between the two sections.

  Here is the template of the report:

  <template>
  ## 🌟 1. Basic Token Information
  Create a table that details the token’s identification metrics. Make sure items in the first column are bold. 
  The table header is "Category" and "Information".

  Here are the metrics:

  * Token Name 
  * Symbol 
  * Contract Address (e.g., 0x8443f091997f06a61670b735ed92734f5628692f, it needs to be the address on BNB Smart Chain)
  * Blockchain (e.g., BNB Smart Chain)
  * Website 
  * Twitter 
  * Category (e.g., Made in USA, Meme, PolitiFi, please estimate the category based on the project)

  ---

  ## 📈 2. Real-time Market Information
  Present detailed market data in a table format. Make sure items in the first column are bold. 
  The table header is "Category" and "Information". Make sure the number of rows is less than 7.
  </template>
    `;

    const messages = [
      { role: 'user', content: userPrompt },
    ];
    
    // Start the conversation  loop
    let thinkingContent = '';  // Show thinking panel and update thinking result
    setThinking(true);
    while (true) {
      try {
        // Call Claude API
        const response = await anthropic.current.messages.create({
          model: 'claude-3-7-sonnet-20250219',
          system: systemPrompt,
          messages,
          max_tokens: 4000,
          tools: createAnthropicTools(tools.current.getTools()),
        }, { signal: abortController.signal });

        setIsLoading(false);

        const message = response.content[0];

        const toolCalls = response.content.filter(c => c.type === "tool_use");

        const isFinalResponse = toolCalls.length === 0;
        if (isFinalResponse) {
          setThinking(false);
          setManualThinkingPanel(false);
        } else {
          setManualThinkingPanel(true);
        }
        
        // If message has content, display it
        if (message.text) {
          let textToDisplay = message.text;
      
          // For final response, remove anything before "## 🌟 1. Basic Token Information"
          if (isFinalResponse) {
            const basicTokenInfoIndex = textToDisplay.indexOf("## 🌟 1. Basic Token Information");
            if (basicTokenInfoIndex !== -1) {
              textToDisplay = textToDisplay.substring(basicTokenInfoIndex);
            }
            // fullResponse = await streamingPrint(textToDisplay, setResult, fullResponse, true);
          } else {
            thinkingContent = await streamingPrint(textToDisplay, setThinkingResult, thinkingContent, true);
          }
        }
          
        // Add the message to conversation history
        // Only push the role and content fields
        messages.push({
          role: "assistant",
          content: response.content
        });
          
        // If no tool calls, we're done
        if (toolCalls.length === 0) {
          break;
        }
          
        // Format tool calls for display
        // const toolCallsFormatted = toolCalls.map(toolCall => {
        //   const functionName = toolCall.name;

        //   // Special handling for search_service
        //   if (functionName === "search_services" && toolCall.input.query) {
        //     return `> **Searching tools** with query:"${toolCall.input.query}"`;
        //   }
        //   // Special handling for invoke_service
        //   if (functionName === "invoke_service" && toolCall.input.action) {
        //     return `> **Retrieving information** with tool: "${toolCall.input.action}"`;
        //   }
        //   return `> **${functionName}**(${JSON.stringify(toolCall.input)})`;
        // }).join('\n\n');
          
        // Add extra line breaks after tool calls
        // if (toolCallsFormatted.length > 0) {
        //   thinkingContent = await streamingPrint(toolCallsFormatted, setThinkingResult, thinkingContent, true);
        // }

        // Call the tools
        // const results = await tools.current.callTools(createUnifaiTools(toolCalls));

        // if (results.length === 0) {
        //   break;
        // }
          
        // // Transform results to the new format with tool_result type in content array
        // const modifiedResults = results.map(result => ({
        //   role: "user",
        //   content: [
        //     {
        //       type: "tool_result",
        //       tool_use_id: result.tool_call_id,
        //       content: result.content
        //     }
        //   ]
        // }));

        // Below are calling Unifai APIs to show thinking process, but not using the results for the final report. Instead we call APIs to get the results reliably.

        // Format tool calls for display
        toolCalls.map(async toolCall => {
          const functionName = toolCall.name;

          // Special handling for search_service
          if (functionName === "search_services" && toolCall.input.query) {

            const toolsToPrint = ["TokenAnalysis/analyzeToken", "OKX DeFi/getTokenAndDefiAssets", "SerpAPI/financeSearch", "SerpAPI/newsSearch"];
            thinkingContent = await streamingPrint(`> **Searching tools** with query:"${toolCall.input.query}"`, setThinkingResult, thinkingContent, true);
            await new Promise(resolve => setTimeout(resolve, 200 + Math.floor(Math.random() * 1000)));
            thinkingContent = await streamingPrint(`> **Retrieving information** with tool:"${toolsToPrint[Math.floor(Math.random() * toolsToPrint.length)]}"`, setThinkingResult, thinkingContent, true);
            // Random wait time between 2-5 seconds
            await new Promise(resolve => setTimeout(resolve, 2000 + Math.floor(Math.random() * 3000)));
          }
        });

        const modifiedResults = toolCalls.map(toolCall => ({
          role: "user",
          content: [
            {
              type: "tool_result",
              tool_use_id: toolCall.id,
              content: ""
            }
          ]
        }));
          
        // Add tool results to messages and continue the conversation
        messages.push(...modifiedResults);

        if (toolCalls.length > 0 && messages.length > 7) {
          setThinking(false);
          setManualThinkingPanel(false);
          break;
        }
        
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('API request was aborted');
          break;
        } else {
          throw error;
        }
      }
    }

    return fullResponse;
  };

  /**
   * Generates a Honest Score report for a given project
   * @param {Object} project - The project data
   * @param {string} fullResponse - The full response string
   * @param {AbortController} abortController - The abort controller
   * @returns {Promise<string>} The generated report
   */
  const generateHonestScoreReport = async (project, fullResponse, abortController) => {
    // Create Honest Score table
    let systemPrompt = `You are a deep research analyst to generate Honest Score for Binance tokens. You have access to detailed project data and create a table of scores.`;
    let userPrompt = `
  Here is a template of the score section you need to generate:
  <example>
  ## 💎 3. Honest Score Analysis

  ### Score: 97/100

  | Category                                    | Score      |
  |---------------------------------------------|------------|
  | Timeliness of Unlocks vs. Schedule          | x1/y1      |
  | Premature Token Movements                   | x2/y2      |
  | Wallet Tagging Clarity                      | x3/y3      |
  | Supply vs. Theoretical Schedule             | x4/y4      |
  | Response to Controversy / Transparency      | x5/y5      |
  | **Total**                                   | **X/100**  |

  </example>

  I will provide you a project token analysis report for ${project.project_name} (${project.ticker}). 
  Please extract the honest score of each category and create a table similar to the example. File in the "xn" and "yn" number with the scores of each category from the report.
  Make sure the x1 + x2 + x3 + x4 + x5 = X, and y1 + y2 + y3 + y4 + y5 = 100.

  Below is the project token analysis report for ${project.project_name} (${project.ticker}):

  ${project.text}
    `;
    const stream = await openai.current.chat.completions.create({
      messages: [{ role: "system", content: systemPrompt }, { role: "user", content: userPrompt }],
      model: "gpt-4o",
      stream: true,
    }, { signal: abortController.signal });

    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content || '';
      if (content) {
        fullResponse += content;
        setResult(fullResponse);
      }
    }

    // Create Honest Score score details section.
    systemPrompt = `You are a deep research analyst to modify the order of the text in a report. You only need to modify the order of the text in the report. You do not need to add or remove any content.`;
    userPrompt = `
  You need to modify the order of the text in the report. You only need to modify the order of the text in the report. You do not need to add or remove any content.
  Move lines that are related to the following topics to the top:
  * Timeliness of Unlocks vs. Schedule
  * Premature Token Movements
  * Wallet Tagging Clarity
  * Supply vs. Theoretical Schedule
  * Response to Controversy / Transparency
  * Final scores

  Put the rest of the text unchanged and after the above topics.
  Please make sure to include every single reference URL.

  The below is the original report:

  ${project.text}
    `;

    const scoreDetails = await openai.current.chat.completions.create({
      messages: [{ role: "system", content: systemPrompt }, { role: "user", content: userPrompt }],
      model: "gpt-4o",
      stream: true,
    }, { signal: abortController.signal });
    
    fullResponse += "\n\n### Score Details\n\n";
    setResult(fullResponse);

    for await (const chunk of scoreDetails) {
      const content = chunk.choices[0]?.delta?.content || '';
      if (content) {
        fullResponse += content;
        setResult(fullResponse);
      }
    }

    return fullResponse;
  };

  /**
   * Streams text content with a chunked approach for better performance
   * @param {string} text - The text content to stream
   * @param {Function} setterFunction - The state setter function to update with the content
   * @param {string} currentContent - The current content to append to
   * @returns {string} - The updated content
   */
  const streamingPrint = async (text, setterFunction, currentContent = '', addLineBreak = false) => {
    let updatedContent = currentContent;
    
    // Adjust chunk size based on text length
    const chunkSize = Math.max(5, Math.floor(text.length / 100));
    
    // Use a batching approach to reduce re-renders
    let batchedContent = '';
    const batchSize = 3; // Number of chunks to batch before updating state
    let batchCount = 0;
    
    for (let i = 0; i < text.length; i += chunkSize) {
      // Get the next chunk of text
      const chunk = text.substring(i, Math.min(i + chunkSize, text.length));
      
      // Add a small delay between chunks
      await new Promise(resolve => setTimeout(resolve, 5));
      
      batchedContent += chunk;
      batchCount++;
      
      // Only update the state after collecting a batch of chunks or at the end
      if (batchCount >= batchSize || i + chunkSize >= text.length) {
        updatedContent += batchedContent;
        setterFunction(updatedContent);
        batchedContent = '';
        batchCount = 0;
      }
    }
    
    // Only add line breaks at the end if needed
    if (addLineBreak) {
      updatedContent += '\n\n';
      setterFunction(updatedContent);
    }
    
    return updatedContent;
  };

  // Handle project selection
  /**
 * Fetches token data from APIs and generates markdown tables
 * @param {string} ticker - The ticker of the project/token
 * @returns {Promise<string>} - Markdown formatted token information
 */
const getTokenData = async (ticker) => {
  try {
    // Fetch basic token info
    const infoResponse = await fetch(`https://cmc-api-five.vercel.app/api/crypto/info?symbol=${ticker.toUpperCase()}`);
    const infoData = await infoResponse.json();
    
    // Fetch real-time token info
    const quotesResponse = await fetch(`https://cmc-api-five.vercel.app/api/crypto/quotes?symbol=${ticker.toUpperCase()}`);
    const quotesData = await quotesResponse.json();
    
    // Check if we have valid data
    if (infoData.status.error_code !== 0 || quotesData.status.error_code !== 0) {
      return "Error fetching token data. Please try again later.";
    }
    
    // Extract token ID and data
    const tokenInfo = infoData.data[ticker.toUpperCase()][0];
    const tokenQuotes = quotesData.data[ticker.toUpperCase()][0];
    
    if (!tokenInfo || !tokenQuotes) {
      return "No data found for the specified token.";
    }
    
    // Generate basic information table
    let markdown = "## 🌟 1. Basic Token Information\n";
    markdown += "|Category|Information|\n|---|---|\n";
    markdown += `|Token Name|${tokenInfo.name}|\n`;
    markdown += `|Symbol|${tokenInfo.symbol}|\n`;
    
    // Get contract address (preferring BNB Smart Chain)
    let contractAddress = "Not available";
    if (tokenInfo.contract_address && tokenInfo.contract_address.length > 0) {
      // Try to find BNB Smart Chain address
      const bnbAddress = tokenInfo.contract_address.find(addr => 
        addr.platform && addr.platform.name && 
        addr.platform.name.toLowerCase().includes("bnb"));
      
      contractAddress = bnbAddress ? bnbAddress.contract_address : tokenInfo.contract_address[0].contract_address;
    }
    
    markdown += `|Contract Address|${contractAddress}|\n`;
    
    // Website
    const website = tokenInfo.urls && tokenInfo.urls.website && tokenInfo.urls.website.length > 0 
      ? tokenInfo.urls.website[0] 
      : "Not available";
    markdown += `|Website|${website}|\n`;
    
    // Twitter
    const twitter = tokenInfo.urls && tokenInfo.urls.twitter && tokenInfo.urls.twitter.length > 0 
      ? tokenInfo.urls.twitter[0] 
      : "Not available";
    markdown += `|Twitter|${twitter}|\n`;
    
    // Description
    const description = tokenInfo.description || "Not available";
    markdown += `|Description|${description}|\n\n`;
    markdown += `\n\n---\n\n`;
    
    // Generate real-time market information table
    markdown += "## 📈 2. Real-time Market Information\n";
    markdown += "|Category|Information|\n|---|---|\n";
    
    const usdQuote = tokenQuotes.quote && tokenQuotes.quote.USD ? tokenQuotes.quote.USD : {};
    
    // Select top 7 most important real-time data
    markdown += `|Current Price|$${usdQuote.price ? usdQuote.price.toLocaleString(undefined, {maximumFractionDigits: 8}) : "N/A"}|\n`;
    markdown += `|Market Cap|$${usdQuote.market_cap ? usdQuote.market_cap.toLocaleString() : "N/A"}|\n`;
    markdown += `|24h Volume|$${usdQuote.volume_24h ? usdQuote.volume_24h.toLocaleString() : "N/A"}|\n`;
    markdown += `|24h Change|${usdQuote.percent_change_24h ? usdQuote.percent_change_24h.toFixed(2) + "%" : "N/A"}|\n`;
    markdown += `|7d Change|${usdQuote.percent_change_7d ? usdQuote.percent_change_7d.toFixed(2) + "%" : "N/A"}|\n`;
    markdown += `|Market Dominance|${usdQuote.market_cap_dominance ? usdQuote.market_cap_dominance.toFixed(2) + "%" : "N/A"}|\n`;
    markdown += `|Circulating Supply|${tokenQuotes.circulating_supply ? tokenQuotes.circulating_supply.toLocaleString() : "N/A"} ${tokenInfo.symbol}|\n\n`;
    markdown += `\n\n---\n\n`;
    
    return markdown;
  } catch (error) {
    console.error("Error fetching token data:", error);
    return "Error fetching token data. Please try again later.";
  }
};

const handleProjectSelect = async (projectName, ticker, year) => {
    setIsLoading(true);
    setThinkingResult('');
    setResult('');
    
    // Update token data with selected project
    setTokenData(prev => ({
      ...prev,
      year,
      projectName: projectName,
      ticker: ticker,
    }));
    
    try {
      // Set step to result and start request
      setStep('result');
      
      // Find the project that matches both project name and ticker
      const project = filteredProjects[year].find(p => 
        p.project_name === projectName && p.ticker === ticker
      );

      if (!project) {
        setResult('No data found for the selected project.');
        return;
      }

      // Create an abort controller
      const abortController = new AbortController();
      setController(abortController);

      // Initialize full response
      let fullResponse = '';
      
      // Set streaming state
      setStreaming(true);

      // Generate Unifai report
      fullResponse = await generateUnifaiReport(project, fullResponse, abortController);
      // Call CMC API to retrieve token info
      const tokenData = await getTokenData(ticker);
      setWritingReport(true);
      fullResponse = await streamingPrint(tokenData, setResult, fullResponse, true);

      // Generate Honest Score report
      fullResponse = await generateHonestScoreReport(project, fullResponse, abortController);

      setStreaming(false);
      setWritingReport(false);

    } catch (error) {
      console.error('Request error:', error);
      setResult('Sorry, an error occurred during analysis. Please try again later.');
    } finally {
      setIsLoading(false);
    }

  };

  // Handle form input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTokenData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Filter projects based on input
    if (name === 'ticker') {
      filterProjects(value);
    }
  };

  // Filter projects based on search input
  const filterProjects = (searchTerm) => {
    // Split projects from honestScoreReport by years
    const projectsByYear = {};
  
    // Group projects by year
    honestScoreReport.documents.forEach(project => {
      const year = project.year;
      if (!projectsByYear[year]) {
        projectsByYear[year] = [];
      }
      projectsByYear[year].push(project);
    });
  
    if (!searchTerm) {
      setFilteredProjects(projectsByYear);
      return;
    }
  
    const term = searchTerm.toLowerCase();
  
    // Filter projects for each year
    const filteredProjectsByYear = {};
  
    Object.keys(projectsByYear).forEach(year => {
      filteredProjectsByYear[year] = projectsByYear[year].filter(project => 
        project.project_name.toLowerCase().includes(term) || 
        project.ticker.toLowerCase().includes(term)
      );
    });
  
    setFilteredProjects(filteredProjectsByYear);
  };

  // Return to home page
  const handleGoBack = () => {
    navigate('/');
  };

  // 用于存储当前API请求的控制器
  const [controller, setController] = useState(null);

  // Return to form page or interrupt API output
  const handleBackToForm = () => {
    // Abort all pending requests with one controller
    if (controller) {
      controller.abort();
      setController(null);
    }
    
    // Reset all states regardless of current state
    setIsLoading(false);
    setStep('form');
    setResult('');
    setThinking(false);
    setThinkingResult('');
    setManualThinkingPanel(false); // Reset thinking panel to default state
    setStreaming(false);
    setWritingReport(false);
    setFilteredProjects(initialProjectsByYear);
    
    // Reset token data to initial state
    setTokenData({
      projectName: '',
      ticker: '',
      year: '',
    });
    
    // Reset controller
    setController(null);
    
    // Reset any scroll position in the thinking content area
    if (thinkingContentRef.current) {
      thinkingContentRef.current.scrollTop = 0;
    }
  };

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [controller]);

  return (
    <div className="w-full min-h-screen bg-element-lightwhite overflow-hidden relative">
      {/* Background image */}
      <img
        className="w-full h-full object-cover absolute top-0 left-0 z-0 opacity-40"
        alt="Background"
        src={bgMain}
      />
      
      {/* Content area */}
      <div className="relative z-10 px-4 min-h-screen flex flex-col w-full max-w-full h-screen overflow-hidden">
        {/* 绝对定位的返回按钮 */}
        <motion.button 
          onClick={step === 'form' ? handleGoBack : handleBackToForm}
          className="absolute top-6 left-4 flex items-center justify-center bg-white rounded-full p-2 shadow-md w-10 h-10 z-30"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>

        {/* Title */}
        <motion.div 
          className="text-center mb-4 mt-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="text-3xl font-bold text-[#1c1f25]">Token Deep Research</h1>
          <p className="text-[#676C77] mt-2">
            {step === 'form' ? 'Select a token to start' : `Honest Score report for ${tokenData.projectName} (${tokenData.ticker})`}
          </p>
        </motion.div>
        
        <AnimatePresence mode="wait">
          {/* Form page */}
          {step === 'form' && (
            <motion.div 
              className="w-full bg-white/90 backdrop-blur-sm rounded-[16px] p-6 shadow-lg h-[calc(100vh-160px)] overflow-hidden flex flex-col"
              key="form"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search Token Ticker</label>
                  <motion.input 
                    whileFocus={{ scale: 1.02 }}
                    type="text" 
                    name="ticker" 
                    placeholder="Example: BNB" 
                    value={tokenData.ticker}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-[#f784ff] focus:border-transparent outline-none transition-all"
                  />
                </div>
                
                {/* Project Tiles */}
                <div className="mt-4 flex-1 overflow-y-auto pb-4" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', scrollbarWidth: 'thin' }}>
                    {/* Dynamically render projects by year */}
                    {Object.keys(filteredProjects).sort().reverse().map(year => (
                      filteredProjects[year].length > 0 && (
                        <div key={year} className={year !== Object.keys(filteredProjects).sort().reverse()[0] ? "mb-4" : ""}>
                          <div className="text-sm font-medium text-gray-500 mb-2">{year}</div>
                          <div className="flex flex-wrap gap-2">
                            {filteredProjects[year].map((project, index) => (
                              <motion.div
                                key={`${year}-${index}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleProjectSelect(project.project_name, project.ticker, year)}
                                className="bg-white/80 hover:bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer transition-all shadow-sm hover:shadow-md"
                              >
                                <div className="font-medium text-sm">{project.project_name}</div>
                                <div className="text-xs text-[#f784ff]">{project.ticker}</div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
              </div>
            </motion.div>
          )}
          
          {/* Result page */}
          {step === 'result' && (
            <motion.div 
              className="w-full bg-white/90 backdrop-blur-sm rounded-[16px] p-6 shadow-lg flex flex-col h-[calc(100vh-160px)] overflow-hidden"
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              {/* Loading animation */}
              {isLoading && !result && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <motion.div
                    className="w-16 h-16 border-4 border-[#f784ff] border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <p className="mt-4 text-[#676C77]">Analyzing token data...</p>
                </div>
              )}
              
              {/* Canvas panel with thinking and result sections as isolated vertical panels */}
              <div className="flex flex-col h-[calc(100vh-200px)] overflow-hidden gap-4" style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Thinking panel - isolated at the top */}
                {!isLoading && (
                  <div 
                    className="bg-[#f9f0ff] rounded-lg overflow-hidden border border-[#f784ff]/30 z-10"
                    style={{
                      height: 'auto',
                      minHeight: '50px',
                      maxHeight: manualThinkingPanel ? '40vh' : '50px',
                      transition: 'max-height 0.3s',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div className="p-3 pt-3 h-full flex flex-col">
                      {/* Header - always visible */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <motion.img 
                            src={require("../images/pinai_logo.png")} 
                            alt="Thinking" 
                            className="w-6 h-6"
                            style={{ transformOrigin: 'center' }}
                            animate={thinking ? { rotate: 360 } : undefined}
                            transition={thinking ? { duration: 2, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                          />
                          <div className="text-[#f784ff] font-medium flex items-center ml-2">
                            {thinking && (
                              // <ThinkingAnimation key="thinking-animation" text="PIN AI is thinking..." />
                              
                              // Thinking Animation
                              <div className={`inline-block relative`}>
                                {/* Base text with original color */}
                                <span>PIN AI is thinking...</span>
                                {/* Animated gradient overlay that only affects the text */}
                                <motion.div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "linear-gradient(90deg, currentColor 20%, rgba(255,255,255,0.8) 50%, currentColor 80%)",
                                    backgroundSize: "200% 100%",
                                    WebkitBackgroundClip: "text",
                                    backgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    textFillColor: "transparent",
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                    pointerEvents: "none"
                                  }}
                                  animate={{
                                    backgroundPosition: ["100% 0", "0% 0", "-100% 0"]
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "linear"
                                  }}
                                >
                                  PIN AI is thinking...
                                </motion.div>
                              </div>
                              
                            )}
                            {!thinking && (
                              <span>PIN AI Thinking Log </span>
                            )}
                          </div>
                        </div>
                        <motion.button
                          className="text-[#f784ff] hover:bg-[#f784ff]/10 rounded-full p-1 transition-colors"
                          onClick={() => setManualThinkingPanel(!manualThinkingPanel)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            style={{ transform: manualThinkingPanel ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </motion.button>
                      </div>
                      
                      {/* Content panel - toggleable */}
                      {manualThinkingPanel && thinkingResult && (
                        <div 
                          className="flex-1 mt-2 overflow-hidden"
                        >
                          <div 
                            ref={thinkingContentRef}
                            className="text-sm text-[#1c1f25] bg-white/50 p-3 rounded overflow-y-auto h-full" 
                          >
                            <MarkdownRenderer content={thinkingResult} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Result content - isolated at the bottom */}
                {result && (
                  <div 
                    className="flex-1 flex flex-col bg-white rounded-lg border border-gray-200"
                    style={{
                      height: 'auto',
                      flex: 1,
                      minHeight: manualThinkingPanel ? '50%' : 'calc(100% - 50px - 1rem)'
                    }}
                  >
                    <div className="p-3 border-b border-gray-200 bg-white rounded-t-lg">
                      <div className="text-[#f784ff] font-medium">
                        {writingReport ? (
                          <div className="writing-report-container">

                            {/* Thinking Animation */}
                            <div className={`inline-block relative`}>
                              {/* Base text with original color */}
                              <span>Writing report...</span>
                              {/* Animated gradient overlay that only affects the text */}
                              <motion.div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: "linear-gradient(90deg, currentColor 20%, rgba(255,255,255,0.8) 50%, currentColor 80%)",
                                  backgroundSize: "200% 100%",
                                  WebkitBackgroundClip: "text",
                                  backgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  textFillColor: "transparent",
                                  display: "inline-block",
                                  whiteSpace: "nowrap",
                                  pointerEvents: "none"
                                }}
                                animate={{
                                  backgroundPosition: ["100% 0", "0% 0", "-100% 0"]
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  ease: "linear"
                                }}
                              >
                                Writing report...
                              </motion.div>
                            </div>

                          </div>
                        ) : "Report is ready!"}
                      </div>
                    </div>
                    {/* Main content */}
                    <div id={resultCardId} className="bg-white rounded-lg p-4 shadow-sm flex-1 overflow-y-auto">
                      <div className="mb-4">
                        <h1 className="text-xl font-bold mb-2">{tokenData.ticker.toUpperCase()} - Deep Research Report to Unlock Honest Score </h1>
                        <div className="text-sm text-gray-500 flex items-center">Performed by PINAI <img src={pinaiLogo} alt="PINAI Logo" className="ml-1 h-4" /></div>
                      </div>
                      <div className="text-[#1c1f25] leading-relaxed">
                        <MarkdownRenderer content={result} increaseLineSpace={true} />
                      </div>
                    </div>
                    {/* 分享按钮 - 位于结果底部 */}
                    {!streaming && (
                      <>
                        <div className="flex flex-col items-center py-2 border-t border-gray-100">
                          <div className="w-full flex justify-center mb-2">
                            <button 
                              onClick={() => setShowHonestScorePanel(true)}
                              className="text-sm text-gray-500 underline hover:text-gray-700 transition-colors"
                            >
                              What's Honest Score?
                            </button>
                          </div>
                          <div className="w-full max-w-[90%]">
                            <ShareButtons
                              cardId={resultCardId}
                              data={{
                                tag: tokenData.ticker.toUpperCase(),
                                title: `Deep Research Report to Unlock Honest Score`,
                                investmentStyle: `Performed by PINAI`
                              }}
                              featureName="Deep Research"
                              shareText={`See my token research and then try yours: https://dapp.pinai.tech\n${tokenData.ticker.toUpperCase()}`}
                              compact={true}
                            />
                          </div>
                        </div>
                        
                        {/* Honest Score Explanation Panel */}
                        <AnimatePresence>
                          {showHonestScorePanel && (
                            <>
                              {/* Backdrop */}
                              <motion.div 
                                className="fixed inset-0 bg-black/60 z-50"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                onClick={() => setShowHonestScorePanel(false)}
                              />
                              
                              {/* Panel */}
                              <motion.div 
                                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 max-h-[50vh] overflow-y-auto"
                                initial={{ y: '100%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '100%' }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                              >
                                <div className="p-4">
                                  <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold">Understanding Honest Score</h3>
                                    <motion.button
                                      className="p-1 rounded-full hover:bg-gray-100"
                                      onClick={() => setShowHonestScorePanel(false)}
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </motion.button>
                                  </div>
                                  <div className="text-gray-700 leading-relaxed space-y-4">
                                    <p className="text-sm font-medium">Cryptocurrency projects often lock large token supplies, releasing them via vesting schedules that require strict adherence to promote investor trust. Binance Research publishes official token allocations and release details, encouraging verification via on-chain data. Critics say misleading token practices distort market signals. We introduce a 100-point "Honest Score" system to evaluate adherence to planned releases, ensuring overall transparency and accountability.</p>
                                    
                                    <div className="mt-4">
                                      <h4 className="text-base font-bold text-gray-800 mb-2">Honest Score Components (100 Points Total)</h4>
                                      <p className="text-sm mb-3">The Honest Score is broken down into five categories reflecting different aspects of honest token release behavior. Each category is assigned a maximum point value, and projects earn points based on performance in that area.</p>
                                      
                                      <div className="space-y-3 mt-2">
                                        <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-yellow-500">
                                          <h5 className="font-bold text-gray-800">Timeliness of Unlocks vs. Schedule <span className="text-yellow-600 ml-1">25 points</span></h5>
                                          <p className="text-sm mt-1">Are tokens unlocked only at the times promised in the Binance Research schedule (no earlier)?</p>
                                        </div>
                                        
                                        <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500">
                                          <h5 className="font-bold text-gray-800">Premature Token Movements <span className="text-blue-600 ml-1">20 points</span></h5>
                                          <p className="text-sm mt-1">Are "locked" tokens refraining from any transfers before their official unlock (no sneaky early moves to other wallets or exchanges)?</p>
                                        </div>
                                        
                                        <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-green-500">
                                          <h5 className="font-bold text-gray-800">Clarity of Wallet Tagging & Transparency <span className="text-green-600 ml-1">15 points</span></h5>
                                          <p className="text-sm mt-1">How clearly are token-holding wallets identified (team, foundation, investors, etc.), and is the on-chain flow of tokens easy to track and verify?</p>
                                        </div>
                                        
                                        <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-purple-500">
                                          <h5 className="font-bold text-gray-800">Deviation of Supply from Theoretical Schedule <span className="text-purple-600 ml-1">25 points</span></h5>
                                          <p className="text-sm mt-1">Does the circulating supply over time match the theoretical supply if the schedule was followed exactly, or is there a significant divergence?</p>
                                        </div>
                                        
                                        <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-red-500">
                                          <h5 className="font-bold text-gray-800">Responsiveness to Vesting Controversies <span className="text-red-600 ml-1">15 points</span></h5>
                                          <p className="text-sm mt-1">If any issues or community concerns arose around token unlocks/vesting, how transparently and promptly did the team respond or adjust their behavior?</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            </>
                          )}
</AnimatePresence>
                      </>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DeepResearchDetail;
